import { useState } from 'react';
import { Loader2, MessageSquarePlus, Plus } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAgentCard } from '../components/AgentDock';
import { InlineError } from '../components/InlineError';
import { PageLoader } from '../components/PageLoader';
import PlanCard, { usePlanMetrics, usePlans, usePlanWrite } from '../components/PlanCard';
import { useTasks } from '../components/TaskCard';

const input = 'w-full text-sm px-3 py-1.5 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand';

function NewPlan({ onChanged, onClose }: { onChanged: () => void; onClose: () => void }) {
    const { sections } = useWorkspace();
    const { run, busy, error } = usePlanWrite(onChanged);
    const [name, setName] = useState('');
    const [due, setDue] = useState('');
    const [description, setDescription] = useState('');
    const [area, setArea] = useState('');

    const save = async () => {
        const ok = await run('/plans', 'POST', {
            name, due_on: due || null, description: description || null, section_id: area || null,
        });
        if (ok) onClose();
    };

    return (
        <div className="border border-softgrey rounded-xl bg-white p-4 space-y-2">
            <h4 className="navaris-h3">New plan</h4>
            <div className="grid gap-2 sm:grid-cols-3">
                <input aria-label="Plan name" placeholder="e.g. Plan to 31 March 2027" value={name}
                       onChange={e => setName(e.target.value)} className={`${input} sm:col-span-2`} />
                <input aria-label="Due by" type="date" value={due} onChange={e => setDue(e.target.value)} className={input} />
            </div>
            <textarea aria-label="What the plan is for" placeholder="What the plan is for" rows={2} value={description}
                      onChange={e => setDescription(e.target.value)} className={input} />
            <select aria-label="Business area" value={area} onChange={e => setArea(e.target.value)} className={input}>
                <option value="">The whole business</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {error && <InlineError message={error} />}
            <div className="flex items-center gap-3">
                <button onClick={save} disabled={busy || !name.trim() || !due}
                        className="navaris-btn-compact">
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Set up the plan
                </button>
                <button onClick={onClose} className="text-[13px] font-medium text-coolslate hover:text-graphite">Cancel</button>
            </div>
        </div>
    );
}

// The business's plans: what it has committed to reach by when, each milestone
// measured on the Model where it can be.
export default function PlansPage() {
    const { plans, reload } = usePlans();
    const metrics = usePlanMetrics();
    const { tasks, reload: reloadTasks } = useTasks();
    const { startChat } = useAgentCard();
    const [adding, setAdding] = useState(false);
    const [showClosed, setShowClosed] = useState(false);

    if (plans === null || metrics === null) return <PageLoader label="Loading the plans..." />;

    const open = plans.filter(p => p.status === 'active' || p.status === 'draft');
    const closed = plans.filter(p => p.status === 'done' || p.status === 'dropped');
    const changed = () => { reload(); reloadTasks(); };
    const card = (p: typeof plans[number]) => (
        <PlanCard key={p.id} plan={p} metrics={metrics.metrics} currency={metrics.currency}
                  tasks={tasks ?? []} onChanged={changed} />
    );

    return (
        <div data-agent-view>
            <div className="pb-6 mb-6 border-b border-softgrey flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                    <h3 className="text-lg font-semibold">Plans</h3>
                    <p className="text-sm text-coolslate mt-1">
                        What the business has committed to reach, and by when. A milestone measured on the
                        Model shows how far it has got and is marked achieved when its figure reaches the target.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={() => startChat('quarterly-planning')}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-dark hover:underline">
                        <MessageSquarePlus className="w-4 h-4" /> Plan the quarter with Navaris
                    </button>
                    {!adding && (
                        <button onClick={() => setAdding(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg bg-brand text-white hover:bg-brand-dark">
                            <Plus className="w-3.5 h-3.5" /> New plan
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-4">
                {adding && <NewPlan onChanged={reload} onClose={() => setAdding(false)} />}
                {open.map(card)}
                {open.length === 0 && !adding && (
                    <p className="text-sm text-coolslate">
                        No plans yet. Set one up here, or ask Navaris to plan the quarter with you.
                    </p>
                )}
                {closed.length > 0 && (
                    <div>
                        <button onClick={() => setShowClosed(v => !v)}
                                className="text-[13px] font-medium text-coolslate hover:text-graphite">
                            {showClosed ? 'Hide' : 'Show'} {closed.length} done or dropped plan{closed.length === 1 ? '' : 's'}
                        </button>
                        {showClosed && <div className="mt-3 space-y-4">{closed.map(card)}</div>}
                    </div>
                )}
            </div>
        </div>
    );
}
